import { useCallback, useState } from "react";
import styled from "styled-components";
import EditIcon from "../../../../../customIcons/Edit";
import NexaTypography from "../../../../shared/NexaTypography";
import ErrorMessage from "../../../../shared/ErrorMessage";
import Button from "../../../../shared/Button";
import { MAX_CHILD_AGE, MIN_CHILD_AGE } from "../../../../../constants/hotelConstants";
import { useTranslation } from "../../../../../i18n";
import { dispatchCustomEvent } from "../../../../../services/widgetEventsService";
import { WidgetGATriggerEvents } from "../../../../../enums/WidgetEvents";

const CHILDREN_AGE_OPTIONS = Array(MAX_CHILD_AGE - MIN_CHILD_AGE + 1)
  .fill(null)
  .map((_, index) => MIN_CHILD_AGE + index);

interface Props {
  childIndex: number;
  hasError?: boolean;
  onChange(age: number | null): void;
  value: number | null;
}

const ChildAgeSelect = ({ childIndex, hasError, onChange, value }: Props) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const handleChange = useCallback(
    (value: number | null) => {
      onChange(value);
      setIsHovered(false);
    },
    [onChange]
  );

  return typeof value === "number" ? (
    <ChildAgeLabel>
      <Text>{`${t("tix_search_form_child")} ${childIndex} ${t("tix_search_form_age")}`}</Text>
      <AgeButton
        fontType="Regular"
        color="white"
        onClick={() => {
          dispatchCustomEvent({
            widgetEvent: WidgetGATriggerEvents.HotelsExtendedSearchChildAgeEdit,
          });
          handleChange(null);
        }}
        onMouseOver={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered ? <EditIcon /> : value.toString()}
      </AgeButton>
    </ChildAgeLabel>
  ) : (
    <>
      <SelectAgeContainer hasError={hasError}>
        <Text>{t("tix_search_form_select_child_age", { index: childIndex })}</Text>
        <AgeButtons>
          {CHILDREN_AGE_OPTIONS.map((age) => (
            <AgeButton
              key={age}
              bordered
              fontType="Regular"
              color="white"
              onClick={() => handleChange(age)}
            >
              {age.toString()}
            </AgeButton>
          ))}
        </AgeButtons>
      </SelectAgeContainer>
      {hasError && (
        <ErrorMessageContainer>
          <ErrorMessage
            error="tix_search_form_hotels_search_form_child_age_err"
            options={{ index: childIndex }}
          />
        </ErrorMessageContainer>
      )}
    </>
  );
};

const ChildAgeLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectAgeContainer = styled.div<{ hasError?: boolean }>`
  background: ${({ theme }) => theme.lightBackground};
  border: 1px solid
    ${({ theme, hasError }) => (hasError ? theme.errorColor : theme.lightBackground)};
  padding: 9px;
`;

const AgeButtons = styled.div`
  margin: 5px -5px -5px -5px;
  display: flex;
  flex-wrap: wrap;

  button {
    margin: 5px;
  }
`;

const AgeButton = styled(Button)`
  font-size: 11px;
  max-width: 25px;
  min-width: 25px;
  max-height: 25px;
  min-height: 25px;
  padding: 0;

  svg {
    height: 11px;
    width: 11px;
  }
`;

const Text = styled(NexaTypography)`
  font-size: 11px;
  line-height: 11px;
`;

const ErrorMessageContainer = styled.div`
  & > * {
    border: none;
    padding: 10px 0 0 0;
    min-height: auto;
  }

  & * {
    font-size: 11px;
  }
`;

export default ChildAgeSelect;
